import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  Fuel,
  Gauge,
  Clock,
  AlertTriangle,
} from 'lucide-react';

interface FuelAnomaly {
  type: 'mpg_drop' | 'excess_idle' | 'unusual_fill' | 'location_mismatch' | 'frequency_spike' | 'volume_spike';
  description: string;
  severity: 'low' | 'medium' | 'high';
  fuel_entry_id?: string;
  detected_at: string;
  vehicle_id?: string;
  vehicle_name?: string;
  period?: string;
}

interface VehicleAnalytics {
  id: string;
  vehicle_id: string;
  vehicle_name: string;
  period: string;
  total_gallons: number;
  total_cost: number;
  avg_mpg: number;
  idle_hours: number;
  idle_cost: number;
  total_miles: number;
  efficiency_score: number;
  anomalies: FuelAnomaly[];
}

interface FleetFuelSummary {
  total_gallons: number;
  total_cost: number;
  avg_mpg: number;
  total_idle_cost: number;
  total_anomalies: number;
  vehicle_count: number;
  analytics: VehicleAnalytics[];
}

const MOCK_SUMMARY: FleetFuelSummary = {
  total_gallons: 4820,
  total_cost: 18350,
  avg_mpg: 19.4,
  total_idle_cost: 1240,
  total_anomalies: 5,
  vehicle_count: 10,
  analytics: [
    {
      id: '1',
      vehicle_id: 'v1',
      vehicle_name: 'Ford Transit #1',
      period: '2026-03',
      total_gallons: 420,
      total_cost: 1596,
      avg_mpg: 22.1,
      idle_hours: 8.2,
      idle_cost: 82,
      total_miles: 9282,
      efficiency_score: 88,
      anomalies: [],
    },
    {
      id: '2',
      vehicle_id: 'v2',
      vehicle_name: 'Chevy Express #2',
      period: '2026-03',
      total_gallons: 610,
      total_cost: 2318,
      avg_mpg: 13.2,
      idle_hours: 22.5,
      idle_cost: 310,
      total_miles: 8052,
      efficiency_score: 52,
      anomalies: [
        {
          type: 'mpg_drop',
          description: 'MPG dropped to 13.2 (avg 18.4) — 28% below average',
          severity: 'medium',
          detected_at: '2026-03-10T04:00:00Z',
          vehicle_name: 'Chevy Express #2',
          period: '2026-03',
        },
        {
          type: 'excess_idle',
          description: 'Average idle time 3.2 hr/day exceeds 2 hr threshold',
          severity: 'medium',
          detected_at: '2026-03-10T04:00:00Z',
          vehicle_name: 'Chevy Express #2',
          period: '2026-03',
        },
      ],
    },
    {
      id: '3',
      vehicle_id: 'v3',
      vehicle_name: 'Ram ProMaster #3',
      period: '2026-03',
      total_gallons: 380,
      total_cost: 1444,
      avg_mpg: 20.8,
      idle_hours: 6.1,
      idle_cost: 58,
      total_miles: 7904,
      efficiency_score: 91,
      anomalies: [],
    },
  ],
};

function getAnomalyBadgeStyle(severity: 'low' | 'medium' | 'high'): { bg: string; text: string } {
  const map = {
    low: { bg: 'rgba(59,130,246,0.15)', text: '#3B82F6' },
    medium: { bg: 'rgba(234,179,8,0.15)', text: '#EAB308' },
    high: { bg: 'rgba(239,68,68,0.15)', text: '#EF4444' },
  };
  return map[severity] ?? { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF' };
}

function getAnomalyTypeLabel(type: FuelAnomaly['type']): string {
  const labels: Record<FuelAnomaly['type'], string> = {
    mpg_drop: 'MPG Drop',
    excess_idle: 'Excess Idle',
    unusual_fill: 'Unusual Fill',
    location_mismatch: 'Location Mismatch',
    frequency_spike: 'Frequency Spike',
    volume_spike: 'Volume Spike',
  };
  return labels[type] ?? type;
}

export default function FuelAnalyticsPage() {
  const { data, isLoading } = useApi<FleetFuelSummary>('/api/fuel-analytics/fleet-summary');
  const d = data ?? MOCK_SUMMARY;

  const allAnomalies = d.analytics.flatMap(a =>
    a.anomalies.map(anomaly => ({
      ...anomaly,
      vehicle_id: a.vehicle_id,
      vehicle_name: a.vehicle_name,
    })),
  ).sort((a, b) => {
    const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
    return (order[a.severity] ?? 9) - (order[b.severity] ?? 9);
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fuel Analytics"
        description="Fleet fuel efficiency, idle cost tracking, and fraud detection"
        actions={isLoading ? <Spinner size={16} /> : null}
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Fuel}
          label="Total Fuel Cost"
          value={`$${d.total_cost.toLocaleString()}`}
        />
        <StatCard
          icon={Gauge}
          label="Avg Fleet MPG"
          value={`${d.avg_mpg.toFixed(1)} mpg`}
          accent="success"
        />
        <StatCard
          icon={Clock}
          label="Idle Cost"
          value={`$${d.total_idle_cost.toLocaleString()}`}
          accent={d.total_idle_cost > 1000 ? 'warning' : undefined}
        />
        <StatCard
          icon={AlertTriangle}
          label="Anomalies Detected"
          value={d.total_anomalies}
          accent={d.total_anomalies > 0 ? 'destructive' : 'success'}
        />
      </div>

      {/* Vehicle Analytics Table */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
            <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
              Vehicle Fuel Analytics
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  {['Vehicle', 'Gallons', 'Cost', 'MPG', 'Efficiency', 'Idle Cost', 'Anomalies'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.analytics.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      No fuel analytics for this period
                    </td>
                  </tr>
                ) : (
                  d.analytics.map(a => (
                    <tr key={a.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                      <td className="px-4 py-3">
                        <div className="font-medium" style={{ color: 'var(--foreground)' }}>{a.vehicle_name}</div>
                        <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                          {a.total_miles.toLocaleString()} mi
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--foreground)' }}>
                        {a.total_gallons.toLocaleString()} gal
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--foreground)' }}>
                        ${a.total_cost.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--foreground)' }}>
                        {a.avg_mpg > 0 ? `${a.avg_mpg.toFixed(1)} mpg` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-2 rounded-full overflow-hidden"
                            style={{ width: 80, background: 'var(--glass-border)' }}
                          >
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${a.efficiency_score}%`,
                                background: a.efficiency_score >= 80
                                  ? '#10B981'
                                  : a.efficiency_score >= 60
                                    ? '#EAB308'
                                    : '#EF4444',
                              }}
                            />
                          </div>
                          <span className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>
                            {a.efficiency_score}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: a.idle_cost > 200 ? '#EAB308' : 'var(--foreground)' }}>
                        ${a.idle_cost.toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        {a.anomalies.length > 0 ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            {a.anomalies.length}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>None</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Anomalies Section */}
      {allAnomalies.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
              <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
                Detected Anomalies
              </h2>
            </div>
            <div className="divide-y" style={{ borderColor: 'var(--glass-border)' }}>
              {allAnomalies.map((anomaly, idx) => {
                const badgeStyle = getAnomalyBadgeStyle(anomaly.severity);
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-4 p-4"
                    style={{
                      background: 'var(--glass-bg)',
                      borderBottom: '1px solid var(--glass-border)',
                    }}
                  >
                    <div className="mt-0.5">
                      <AlertTriangle className="w-4 h-4" style={{ color: badgeStyle.text }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm" style={{ color: 'var(--foreground)' }}>
                          {anomaly.vehicle_name ?? 'Unknown Vehicle'}
                        </span>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ background: badgeStyle.bg, color: badgeStyle.text }}
                        >
                          {anomaly.severity.toUpperCase()}
                        </span>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs"
                          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--muted-foreground)' }}
                        >
                          {getAnomalyTypeLabel(anomaly.type)}
                        </span>
                      </div>
                      <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                        {anomaly.description}
                      </p>
                    </div>
                    <div className="text-xs shrink-0" style={{ color: 'var(--muted-foreground)' }}>
                      {anomaly.period ?? new Date(anomaly.detected_at).toLocaleDateString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
